/*  EditOneEmployee.js - Complete Version with All 95 Fields  */
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Spin,
    Typography,
    Space,
    DatePicker,
    Checkbox,
    message,
    Upload,
    Image,
    Switch,
} from "antd";
import {
    FaUser,
    FaUniversity,
    FaMapMarkerAlt,
    FaGraduationCap,
    FaPaperPlane,
    FaIdCard,
    FaFileAlt,
} from "react-icons/fa";
import { PlusOutlined, CloseOutlined, UploadOutlined, EditOutlined, CalendarOutlined, LogoutOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthContext";
import SendMail from "./SendMail";
import NocForm from "./NocForm";
import ViewOneExit from "./hr/ViewOneExit";
import { useNavigate } from "react-router-dom";
import { Beaker } from "lucide-react";
import LeaveCalendar from "./employee/LeaveCalendar";
import ViewOneEmpPerm from "./ViewOneEmpPerm";
import ViewOneEmployeeRecord from "./hr/ViewOneEmployeeRecord";


const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EditOneEmployee = ({ employeeId, isResigned, onBack }) => {

    const navigate = useNavigate()
    /* --------------------------- STATE --------------------------- */
    const [formData, setFormData] = useState({});
    const [form] = Form.useForm();
    const [fileData, setFileData] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeSection, setActiveSection] = useState("personal");
    const [showModal, setShowModal] = useState(false);
    const [docUrl, setDocUrl] = useState("");
    const [errors, setErrors] = useState({});
    const [previewUrl, setPreviewUrl] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [hodList, setHodList] = useState([]);
    const [showUpload, setShowUpload] = useState(false);
    const [signatureUrl, setSignatureUrl] = useState(null);
    const [image, setImage] = useState(null);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [workLocations, setWorkLocations] = useState([]);

    // Dynamic array lengths
    const [eduCount, setEduCount] = useState(1);
    const [careerCount, setCareerCount] = useState(1);
    const [refCount, setRefCount] = useState(1);
    const [childCount, setChildCount] = useState(0);
    const [siblingCount, setSiblingCount] = useState(0);

    const [loadingEmployees, setLoadingEmployees] = useState(false);

    const { authData, backUrl } = useContext(AuthContext);
    const { cid } = useParams();
    const effectiveId = employeeId || cid;

    const [reportingTo, setReportingTo] = useState([])

    const [reportingToLoading, setReportingToLoading] = useState(false);

    const [empCode, setEmpCode] = useState("")
    const [targetEmployee, setTargetEmployee] = useState(null)

    const employeeStatuses = [
        { value: "PROBATION", label: "Probation" },
        { value: "CONFIRMED", label: "Confirmed" },
        { value: "NOTICE_PERIOD", label: "Notice Period" },
        { value: "ABSCONDED", label: "Absconded" },
        { value: "RETRENCHMENT", label: "Retrenchment" },
        { value: "RESIGNED", label: "Resigned" },
    ];

    const onFinish = async (values) => {
        let payload = {
            employee: employeeId,
            requested_status: values.requested_status,
            remarks: values.remarks,
        };

        if (values.requested_status === "PROBATION") {
            payload.probation_end_date = values.probation_end_date.format("YYYY-MM-DD");
        } else if (values.requested_status === "CONFIRMED") {
            payload.final_probation_end_date = values.final_probation_end_date.format("YYYY-MM-DD");
        } else {
            payload.date_of_leaving = values.date_of_leaving.format("YYYY-MM-DD");
            payload.last_working_day = values.last_working_day.format("YYYY-MM-DD");
        }

        try {
            const res = await axios.post(
                `${backUrl}/api/emp/employee-status-change/`,
                payload,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            message.success("Employee status updated successfully");
            form.resetFields();
        } catch (error) {
            console.error(error);
            message.error("Something went wrong");
        }
    };

    const requestedStatus = Form.useWatch("requested_status", form);


    const uploadProps = {
        beforeUpload: (file) => {
            const isImage =
                file.type === "image/png" ||
                file.type === "image/jpeg" ||
                file.type === "image/jpg";

            setImage(file);

            if (!isImage) {
                message.error("Only JPG/PNG images are allowed");
                return Upload.LIST_IGNORE;
            }

            const reader = new FileReader();
            reader.onload = () => {
                setSignatureUrl(reader.result);
            };
            reader.readAsDataURL(file);

            return false;
        },
        maxCount: 1,
        showUploadList: false
    };

    /* --------------------------- FETCH --------------------------- */
    const fetchEmployees = async () => {
        try {
            setLoadingEmployees(true);
            const res = await axios.get(`${backUrl}/api/emp/employees/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });
            setEmployees(res.data.results || []);
        } catch (err) {
            console.error("Error fetching employees:", err);
            message.error("Failed to load employees");
        } finally {
            setLoadingEmployees(false);
        }
    };

    const fetchEmployee = async () => {
        if (!effectiveId) return;
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/employees/${effectiveId}/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const data = res.data || {};

            if (typeof data.child_details === 'string') {
                try {
                    data.child_details = JSON.parse(data.child_details);
                } catch (e) {
                    data.child_details = [];
                }
            }

            if (typeof data.sibling_details === 'string') {
                try {
                    data.sibling_details = JSON.parse(data.sibling_details);
                } catch (e) {
                    data.sibling_details = [];
                }
            }

            setFormData(data);
            setEduCount(data.education_history?.length || 1);
            setCareerCount(data.career_history?.length || 1);
            setRefCount(data.reference_history?.length || 1);
            setChildCount(data.child_details?.length || 0);
            setSiblingCount(data.sibling_details?.length || 0);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load employee");
        } finally {
            setLoading(false);
        }
    };

    // Update the fetchReportingTo function:
    const fetchReportingTo = async () => {
        try {
            setReportingToLoading(true); // ✅ Add this line
            const res = await axios.get(`${backUrl}/api/emp/employees/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setReportingTo(res.data.results)
        }
        catch (error) {
            console.error("Error fetching reporting to list", error);
        } finally {
            setReportingToLoading(false);
        }
    }



    const fetchOrganisations = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        })

        setOrganisations(res.data.results)
    }

    const fetchWorkLocations = async () => {
        const res = await axios.get(`${backUrl}/api/emp/worklocation`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        })

        setWorkLocations(res.data.results)
    }

    const fetchDepartments = async () => {
        const res = await axios.get(`${backUrl}/api/emp/departments`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        })

        setDepartments(res.data.results)
    }

    const fetchDesignations = async () => {
        const res = await axios.get(`${backUrl}/api/emp/designations`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        })

        setDesignations(res.data.results)
    }

    useEffect(() => {
        fetchEmployees();
        fetchOrganisations();
        fetchDepartments();
        fetchDesignations();
        fetchWorkLocations();
        fetchReportingTo();
    }, []);

    useEffect(() => {
        if (effectiveId && authData?.access) fetchEmployee();
    }, [effectiveId, authData, backUrl]);

    /* --------------------------- EDIT / SAVE --------------------------- */
    const toggleEdit = () => {
        if (isEditMode) {
            setIsEditMode(false);
            fetchEmployee();
        } else setIsEditMode(true);
    };

    const validate = () => {
        setErrors({});
        return true;
    };


    const handleGetEmployee = async () => {
        const res = await axios.get(`${backUrl}/api/emp/employees?emp_code=${empCode}`, {
            headers: {
                Authorization: `Bearer ${authData?.access}`
            }
        })
        setTargetEmployee(res.data)
    }

    const submit = async () => {
        if (!validate()) return;
        setIsSubmitting(true);

        const skip = [
            "HSC_diploma_ITI",
            "PG_Phd",
            "SSLC",
            "aadhar_card_copy",
            "appointment_letter",
            "bank_account_details",
            "covid_certificate",
            "degree_certificate",
            "last_three_month_payslip",
            "offer_letter",
            "pan_card_copy",
            "previous_experience_letter",
            "releiving_letter",
            "last_three_months_account_statement",
            "fnf_details",
            "user",
            "organisation_name",
            "department_name",
            "designation_name",
            "reporting_to_name",
            "reporting_to_hod_name",
            "place_of_work_names",
            "employee_work_duration",
            "status",
            "is_active",
            "is_hod",
            "is_hr",
            "is_management",
            "is_ot",
            "is_special",
            "is_shift",
            "is_checkin",
            "is_superuser",
            "is_wedding_today",
            "present_address",
            "send_welcome_note",
            "appointment_letter_triggered",
            "reporting_to_hod",
            "others_1",
            "others2",
            "photo"
        ];

        const integerFields = ['organisation', 'department', 'designation', 'reporting_to', 'role'];
        const arrayFields = ['place_of_work'];
        const dateFields = [
            'date_of_birth',
            'wedding_date',
            'spouse_dob',
            'date_of_joining',
            'probation_end_date',
            'date_of_leaving',
            'last_working_day'
        ];

        // Helper function to format dates to YYYY-MM-DD
        const formatDate = (dateValue) => {
            if (!dateValue) return null;

            // If it's already in YYYY-MM-DD format, return as is
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }

            // Try to parse with dayjs
            const dayjsDate = dayjs(dateValue);
            if (dayjsDate.isValid()) {
                return dayjsDate.format('YYYY-MM-DD');
            }

            return null;
        };

        // Check if any files are being uploaded (excluding photo)
        const fileDataWithoutPhoto = { ...fileData };
        delete fileDataWithoutPhoto.photo;
        const hasFiles = Object.keys(fileDataWithoutPhoto).length > 0 && Object.values(fileDataWithoutPhoto).some(v => v);

        if (hasFiles) {
            // Use FormData if files are present
            const fd = new FormData();

            Object.entries(formData).forEach(([k, v]) => {
                if (skip.includes(k) || v === null || v === undefined) return;

                if (dateFields.includes(k)) {
                    // Format date fields
                    const formattedDate = formatDate(v);
                    if (formattedDate) {
                        fd.append(k, formattedDate);
                    }
                } else if (arrayFields.includes(k)) {
                    // For array fields in FormData, append each item separately
                    const arrayValue = Array.isArray(v) ? v : [v];
                    arrayValue.forEach(item => {
                        fd.append(k, item);
                    });
                } else if (integerFields.includes(k)) {
                    const intValue = parseInt(v, 10);
                    if (!isNaN(intValue)) {
                        fd.append(k, intValue);
                    }
                } else if (Array.isArray(v) || (typeof v === "object" && v !== null)) {
                    fd.append(k, JSON.stringify(v));
                } else {
                    fd.append(k, v);
                }
            });

            // Add files to FormData (excluding photo)
            Object.entries(fileDataWithoutPhoto).forEach(([k, v]) => {
                if (v) fd.append(k, v);
            });

            try {
                await axios.put(`${backUrl}/api/emp/employees/${formData.id}/`, fd, {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                });
                toast.success("Saved successfully");
                setIsEditMode(false);
                setFileData({});
                fetchEmployee();
            } catch (e) {
                console.error("Error details:", e.response?.data);
                toast.error(e.response?.data?.message || e.response?.data?.data || "Save failed");
            } finally {
                setIsSubmitting(false);
            }
        } else {
            // Use JSON if no files
            const payload = {};

            Object.entries(formData).forEach(([k, v]) => {
                if (skip.includes(k) || v === null || v === undefined) return;

                if (dateFields.includes(k)) {
                    // Format date fields
                    const formattedDate = formatDate(v);
                    if (formattedDate) {
                        payload[k] = formattedDate;
                    }
                } else if (arrayFields.includes(k)) {
                    // Ensure array fields are arrays
                    payload[k] = Array.isArray(v) ? v : (v ? [v] : []);
                } else if (integerFields.includes(k)) {
                    const intValue = parseInt(v, 10);
                    payload[k] = !isNaN(intValue) ? intValue : v;
                } else {
                    payload[k] = v;
                }
            });

            try {
                await axios.put(`${backUrl}/api/emp/employees/${formData.id}/`, payload, {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        'Content-Type': 'application/json',
                    },
                });
                toast.success("Saved successfully");
                setIsEditMode(false);
                fetchEmployee();
            } catch (e) {
                console.error("Error details:", e.response?.data);
                toast.error(e.response?.data?.message || e.response?.data?.data || "Save failed");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    /* --------------------------- HANDLERS --------------------------- */
    const onInputChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === "file") setFileData((p) => ({ ...p, [name]: files[0] }));
        else setFormData((p) => ({ ...p, [name]: value }));
    };

    const onDateChange = (name, dateString) => {
        setFormData((p) => ({ ...p, [name]: dateString }));
    };

    const onSelectChange = (name, value) => {
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const updateArray = (arrayName, idx, field, value) => {
        setFormData((prev) => {
            const arr = [...(prev[arrayName] || [])];
            if (!arr[idx]) arr[idx] = {};
            arr[idx][field] = value;
            return { ...prev, [arrayName]: arr };
        });
    };

    const addItem = (setter, arrayName) => {
        setter((count) => count + 1);
        setFormData((prev) => ({
            ...prev,
            [arrayName]: [...(prev[arrayName] || []), {}],
        }));
    };

    const removeItem = (setter, idx, arrayName) => {
        setter((count) => Math.max(0, count - 1));
        setFormData((prev) => ({
            ...prev,
            [arrayName]: prev[arrayName]?.filter((_, i) => i !== idx) || [],
        }));
    };

    const copyPresentToPermanent = (checked) => {
        if (checked) {
            setFormData((prev) => ({
                ...prev,
                permanent_address_for_communication: prev.address_for_communication,
                permanent_area: prev.area,
                permanent_city: prev.city,
                permanent_state: prev.state,
                permanent_country: prev.country,
                permanent_pincode: prev.pincode,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                permanent_address_for_communication: "",
                permanent_area: "",
                permanent_city: "",
                permanent_state: "",
                permanent_country: "",
                permanent_pincode: "",
            }));
        }
    };

    const openDoc = (url) => {
        if (!url) return;
        const ext = url.split(".").pop().toLowerCase();
        const img = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
        if (img.includes(ext)) {
            setDocUrl(url);
            setShowModal(true);
        } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = url.split("/").pop();
            a.target = "_blank";
            a.click();
        }
    };

    const triggerExitInterview = async () => {
        try {
            const res = await axios.post(`${backUrl}/api/payroll/exit-interview/trigger/`, {
                "employee": employeeId
            }, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });
            message.success("Exit Interview Triggered Successfully");
        } catch (err) {
            console.error(err);
            message.error("Failed to trigger exit interview");
        }
    };

    const handleUpdateEmployee = async () => {
        try {
            setIsSubmitting(true);

            const payload = {
                is_ot: formData.is_ot || false,
                is_special: formData.is_special || false,
                is_shift: formData.is_shift || false,
                is_checkin: formData.is_checkin || false,
                is_hr: formData.is_hr || false,
                is_hod: formData.is_hod || false,
                is_superuser: formData.is_superuser || false,
                is_management: formData.is_management || false,
                reporting_to: formData.reporting_to || null,
                // reporting_to_hod: formData.reporting_to_hod || null,
            };

            await axios.put(
                `${backUrl}/api/emp/employees/${effectiveId}/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );

            toast.success("Employee updated successfully");
            fetchEmployee();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Update failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    /* --------------------------- RENDER --------------------------- */
    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "100px 0" }}>
                <Spin size="large" tip="Loading employee..." />
            </div>
        );
    }

    const navigationItems = [
        { key: "personal", label: "Personal", icon: <FaUser /> },
        { key: "employment", label: "Employment", icon: <FaIdCard /> },
        { key: "bank", label: "Bank", icon: <FaUniversity /> },
        { key: "address", label: "Address", icon: <FaMapMarkerAlt /> },
        { key: "education", label: "Education", icon: <FaGraduationCap /> },
        { key: "documents", label: "Documents", icon: <FaFileAlt /> },
        { key: "update", label: "Update Employee", icon: <EditOutlined /> },

        !isResigned
            ? { key: "sendNote", label: "Welcome Note", icon: <FaPaperPlane /> }
            : { key: "triggerNoc", label: "NOC", icon: <FaPaperPlane /> },
        !isResigned
            ? { key: "appointment", label: "Appointment", icon: <FaPaperPlane /> }
            : { key: "exitInterview", label: "View Exit Interview", icon: <FaPaperPlane /> },

        { key: "calendar", label: "Calendar", icon: <CalendarOutlined /> },
        { key: "leaves", label: "Leaves", icon: <LogoutOutlined /> },
        { key: "permissions", label: "Permissions", icon: <SafetyCertificateOutlined /> },
        { key: "status", label: "Change Status", icon: <FaUser /> },
    ];

    return (
        <>
            <ToastContainer position="top-center" autoClose={3000} />
            <button style={{
                color: 'white',
                padding: '5px'
            }} onClick={onBack}>Back</button>

            {/* Navigation */}
            <div className="container mt-4 mb-4">
                <Row justify="center" gutter={[16, 16]}>
                    {navigationItems.map((s) => {
                        const isActive = activeSection === s.key;
                        return (
                            <Col key={s.key}>
                                <div
                                    className="d-flex flex-column align-items-center"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setActiveSection(s.key)}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                                        style={{
                                            width: 50,
                                            height: 50,
                                            backgroundColor: isActive ? "#0d6efd" : "#e9ecef",
                                            color: isActive ? "#fff" : "#6c757d",
                                        }}
                                    >
                                        {s.icon}
                                    </div>
                                    <small className={isActive ? "text-primary fw-bold" : "text-muted"}>
                                        {s.label}
                                    </small>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </div>

            {/* Edit / Save buttons */}
            {!["sendNote", "triggerNoc", "exitInterview", "appointment", "update"].includes(activeSection) && (
                <center className="my-3">
                    {!isEditMode ? (
                        <Button type="primary" style={{ background: "#0e497b" }} onClick={toggleEdit}>
                            Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button type="primary" loading={isSubmitting} style={{ background: "#0e497b" }} onClick={submit}>
                                Save
                            </Button>
                            <Button onClick={toggleEdit}>Cancel</Button>
                        </Space>
                    )}
                </center>
            )}

            {/* SECTION CONTENT */}
            <div className="container px-4">
                {/* PERSONAL SECTION */}
                {activeSection === "personal" && (
                    <>
                        <Title level={3} className="text-center mb-4">Personal Details</Title>
                        <center>
                            <img
                                style={{ width: '200px', height: '200px', borderRadius: '50%', objectFit: 'cover' }}
                                src={fileData.photo ? URL.createObjectURL(fileData.photo) : formData.photo || 'https://via.placeholder.com/200'}
                                alt="Employee"
                            />
                            <br /><br />
                            {isEditMode && (
                                <Form.Item label="Update Photo">
                                    <Input type="file" name="photo" accept="image/*" onChange={onInputChange} />
                                    {fileData.photo && <div style={{ color: 'green', marginTop: 5 }}>New photo selected</div>}
                                </Form.Item>
                            )}
                        </center>
                        <br />
                        <Form layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Salutation">
                                        <Select value={formData.salutation || undefined} onChange={(v) => onSelectChange("salutation", v)} disabled={!isEditMode}>
                                            <Option value="">-- Select --</Option>
                                            <Option value="Mr">Mr</Option>
                                            <Option value="Ms">Ms</Option>
                                            <Option value="Mrs">Mrs</Option>
                                            <Option value="Dr">Dr</Option>
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="Initial">
                                        <Input name="initial" value={formData.initial || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="First Name">
                                        <Input name="first_name" value={formData.first_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Surname">
                                        <Input name="surname" value={formData.surname || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Last Name">
                                        <Input name="last_name" value={formData.last_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Mobile Number">
                                        <Input name="mobile_number" value={formData.mobile_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Email">
                                        <Input name="email_id" value={formData.email_id || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Official Email ID">
                                        <Input name="official_email_id" value={formData.official_email_id || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Aadhaar Number">
                                        <Input name="aadhaar_card_number" value={formData.aadhaar_card_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="PAN Number">
                                        <Input name="pan_card_number" value={formData.pan_card_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Date of Birth">
                                        <DatePicker
                                            style={{ width: "100%" }}
                                            value={formData.date_of_birth ? dayjs(formData.date_of_birth) : null}
                                            onChange={(d, ds) => onDateChange("date_of_birth", ds)}
                                            disabled={!isEditMode}
                                        />
                                    </Form.Item>

                                    <Form.Item label="Place of Birth">
                                        <Input name="place_of_birth" value={formData.place_of_birth || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Mother Tongue">
                                        <Input name="mother_tongue" value={formData.mother_tongue || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Gender">
                                        <Select value={formData.gender || undefined} onChange={(v) => onSelectChange("gender", v)} disabled={!isEditMode}>
                                            <Option value="">-- Select --</Option>
                                            <Option value="MALE">Male</Option>
                                            <Option value="FEMALE">Female</Option>
                                            <Option value="Other">Other</Option>
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="Religion">
                                        <Input name="religion" value={formData.religion || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item label="Caste">
                                        <Input name="caste" value={formData.caste || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Blood Group">
                                        <Input name="blood_group" value={formData.blood_group || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Height (cm)">
                                        <Input name="height" value={formData.height || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Weight (kg)">
                                        <Input name="weight" value={formData.weight || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Marital Status">
                                        <Select value={formData.marital_status || undefined} onChange={(v) => onSelectChange("marital_status", v)} disabled={!isEditMode}>
                                            <Option value="">-- Select --</Option>
                                            <Option value="MARRIED">Married</Option>
                                            <Option value="UNMARRIED">Unmarried</Option>
                                            <Option value="DIVORCED">Divorced</Option>
                                            <Option value="WIDOWED">Widowed</Option>
                                            <Option value="SEPARATED">Separated</Option>
                                        </Select>
                                    </Form.Item>

                                    {formData.marital_status === "Married" && (
                                        <>
                                            <Form.Item label="Wedding Date">
                                                <DatePicker
                                                    style={{ width: "100%" }}
                                                    value={formData.wedding_date ? dayjs(formData.wedding_date) : null}
                                                    onChange={(d, ds) => onDateChange("wedding_date", ds)}
                                                    disabled={!isEditMode}
                                                />
                                            </Form.Item>

                                            <Form.Item label="Spouse Name">
                                                <Input name="spouse_name" value={formData.spouse_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                            </Form.Item>

                                            <Form.Item label="Spouse DOB">
                                                <DatePicker
                                                    style={{ width: "100%" }}
                                                    value={formData.spouse_dob ? dayjs(formData.spouse_dob) : null}
                                                    onChange={(d, ds) => onDateChange("spouse_dob", ds)}
                                                    disabled={!isEditMode}
                                                />
                                            </Form.Item>

                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <Title level={5}>Children</Title>
                                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => addItem(setChildCount, "child_details")} disabled={!isEditMode}>
                                                        Add
                                                    </Button>
                                                </div>

                                                {Array.from({ length: childCount }, (_, i) => (
                                                    <Card key={i} className="mb-2">
                                                        <Space direction="vertical" style={{ width: "100%" }}>
                                                            <Input
                                                                placeholder="Child Name"
                                                                value={formData.child_details?.[i]?.child_name || ""}
                                                                onChange={(e) => updateArray("child_details", i, "child_name", e.target.value)}
                                                                disabled={!isEditMode}
                                                            />
                                                            <DatePicker
                                                                style={{ width: "100%" }}
                                                                placeholder="DOB"
                                                                value={formData.child_details?.[i]?.child_dob ? dayjs(formData.child_details[i].child_dob) : null}
                                                                onChange={(d, ds) => updateArray("child_details", i, "child_dob", ds)}
                                                                disabled={!isEditMode}
                                                            />
                                                            <Select
                                                                placeholder="Gender"
                                                                value={formData.child_details?.[i]?.child_gender || undefined}
                                                                onChange={(v) => updateArray("child_details", i, "child_gender", v)}
                                                                disabled={!isEditMode}
                                                            >
                                                                <Option value="Male">Male</Option>
                                                                <Option value="Female">Female</Option>
                                                                <Option value="Other">Other</Option>
                                                            </Select>
                                                            <Button danger icon={<CloseOutlined />} onClick={() => removeItem(setChildCount, i, "child_details")} disabled={!isEditMode}>
                                                                Remove
                                                            </Button>
                                                        </Space>
                                                    </Card>
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Title level={5}>Siblings</Title>
                                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => addItem(setSiblingCount, "sibling_details")} disabled={!isEditMode}>
                                                Add
                                            </Button>
                                        </div>

                                        {Array.from({ length: siblingCount }, (_, i) => (
                                            <Card key={i} className="mb-2">
                                                <Space direction="vertical" style={{ width: "100%" }}>
                                                    <Input
                                                        placeholder="Sibling Name"
                                                        value={formData.sibling_details?.[i]?.sibling_name || ""}
                                                        onChange={(e) => updateArray("sibling_details", i, "sibling_name", e.target.value)}
                                                        disabled={!isEditMode}
                                                    />
                                                    <DatePicker
                                                        style={{ width: "100%" }}
                                                        placeholder="DOB"
                                                        value={formData.sibling_details?.[i]?.sibling_dob ? dayjs(formData.sibling_details[i].sibling_dob) : null}
                                                        onChange={(d, ds) => updateArray("sibling_details", i, "sibling_dob", ds)}
                                                        disabled={!isEditMode}
                                                    />
                                                    <Select
                                                        placeholder="Gender"
                                                        value={formData.sibling_details?.[i]?.sibling_gender || undefined}
                                                        onChange={(v) => updateArray("sibling_details", i, "sibling_gender", v)}
                                                        disabled={!isEditMode}
                                                    >
                                                        <Option value="Male">Male</Option>
                                                        <Option value="Female">Female</Option>
                                                        <Option value="Other">Other</Option>
                                                    </Select>
                                                    <Input
                                                        placeholder="Profession"
                                                        value={formData.sibling_details?.[i]?.sibling_profession || ""}
                                                        onChange={(e) => updateArray("sibling_details", i, "sibling_profession", e.target.value)}
                                                        disabled={!isEditMode}
                                                    />
                                                    <Button danger icon={<CloseOutlined />} onClick={() => removeItem(setSiblingCount, i, "sibling_details")} disabled={!isEditMode}>
                                                        Remove
                                                    </Button>
                                                </Space>
                                            </Card>
                                        ))}
                                    </div>

                                    {/* <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Title level={5}>Children</Title>
                                            <Button
                                                type="dashed"
                                                icon={<PlusOutlined />}
                                                onClick={() => {
                                                    setChildCount((c) => c + 1);
                                                    setFormData((p) => ({
                                                        ...p,
                                                        child_details: [
                                                            ...(p.child_details || []),
                                                            { child_name: "", child_dob: "" },
                                                        ],
                                                    }));
                                                }}
                                                disabled={!isEditMode}
                                            >
                                                Add
                                            </Button>
                                        </div> */}

                                    {/* {Array.from({ length: childCount }, (_, i) => (
                                            <Card key={i} className="mb-2">
                                                <Space direction="vertical" style={{ width: "100%" }}>
                                                    <Input
                                                        placeholder="Child Name"
                                                        value={formData.child_details?.[i]?.child_name || ""}
                                                        onChange={(e) =>
                                                            updateArray(
                                                                "child_details",
                                                                i,
                                                                "child_name",
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={!isEditMode}
                                                    />
                                                    <DatePicker
                                                        style={{ width: "100%" }}
                                                        placeholder="DOB"
                                                        value={
                                                            formData.child_details?.[i]?.child_dob
                                                                ? dayjs(formData.child_details[i].child_dob)
                                                                : null
                                                        }
                                                        onChange={(d, ds) =>
                                                            updateArray("child_details", i, "child_dob", ds)
                                                        }
                                                        disabled={!isEditMode}
                                                    />
                                                    <Button
                                                        danger
                                                        icon={<CloseOutlined />}
                                                        onClick={() => {
                                                            setChildCount((c) => c - 1);
                                                            setFormData((p) => ({
                                                                ...p,
                                                                child_details: p.child_details.filter((_, idx) => idx !== i),
                                                            }));
                                                        }}
                                                        disabled={!isEditMode}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Space>
                                            </Card>
                                        ))} 
                                    </div>*/}

                                    <Form.Item label="Emergency Contact Person">
                                        <Input name="emergency_contact_person" value={formData.emergency_contact_person || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Emergency Contact Number">
                                        <Input name="emergency_contact_number" value={formData.emergency_contact_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Wedding Date">
                                        <Input name="wedding_date" value={formData.wedding_date || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Spouse Name">
                                        <Input name="spouse_name" value={formData.spouse_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Spouse DOB">
                                        <Input name="spouse_dob" value={formData.spouse_dob || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Father's Profession">
                                        <Input name="father_profession" value={formData.father_profession || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>


                                    <Form.Item label="Father's Name">
                                        <Input name="father_name" value={formData.father_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Father's Profession">
                                        <Input name="father_profession" value={formData.father_profession || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Mother's Name">
                                        <Input name="mother_name" value={formData.mother_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Mother's Profession">
                                        <Input name="mother_profession" value={formData.mother_profession || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Landline Extension">
                                        <Input name="landline_extension" value={formData.landline_extension || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Official CUG Number">
                                        <Input name="official_cug_number" value={formData.official_cug_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <center className="mt-4">
                                <Button type="primary" onClick={() => setActiveSection("employment")}>Next</Button>
                            </center>
                        </Form>
                    </>
                )}

                {/* EMPLOYMENT SECTION */}
                {activeSection === "employment" && (
                    <>
                        <Title level={3} className="text-center mb-4">Employment Details</Title>
                        <Form layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Employee Code">
                                        <Input value={formData.user?.username || ""} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Organisation">
                                        {isEditMode ? (
                                            <Select
                                                showSearch
                                                placeholder="Select Organisation"
                                                value={formData.organisation || undefined}
                                                onChange={(value) => onSelectChange("organisation", value)}
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {organisations.map((org) => (
                                                    <Option key={org.id} value={org.id}>
                                                        {org.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Input value={formData.organisation_name || ""} disabled={!isEditMode} />
                                        )}
                                    </Form.Item>

                                    <Form.Item label="Department">
                                        {isEditMode ? (
                                            <Select
                                                showSearch
                                                placeholder="Select Department"
                                                value={formData.department || undefined}
                                                onChange={(value) => onSelectChange("department", value)}
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {departments.map((dept) => (
                                                    <Option key={dept.id} value={dept.id}>
                                                        {dept.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Input value={formData.department_name || ""} disabled={!isEditMode} />
                                        )}
                                    </Form.Item>

                                    <Form.Item label="Designation">
                                        {isEditMode ? (
                                            <Select
                                                showSearch
                                                placeholder="Select Designation"
                                                value={formData.designation || undefined}
                                                onChange={(value) => onSelectChange("designation", value)}
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {designations.map((desig) => (
                                                    <Option key={desig.id} value={desig.id}>
                                                        {desig.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Input value={formData.designation_name || ""} disabled={!isEditMode} />
                                        )}
                                    </Form.Item>

                                    <Form.Item label="Location">
                                        <Input name="location" value={formData.location || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>


                                    <Form.Item label="Place Of Work">
                                        {isEditMode ? (
                                            <Select
                                                showSearch
                                                placeholder="Select Work Location"
                                                value={formData.place_of_work || undefined}
                                                onChange={(value) => {
                                                    // Store as array even for single selection
                                                    onSelectChange("place_of_work", value ? [value] : [])
                                                }}
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                                }
                                            >
                                                {workLocations.map((loc) => (
                                                    <Option key={loc.id} value={loc.id}>
                                                        {loc.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Input value={formData.place_of_work_names?.[0] || ""} disabled />
                                        )}
                                    </Form.Item>

                                    <Form.Item label="Date of Joining">
                                        <DatePicker
                                            style={{ width: "100%" }}
                                            value={formData.date_of_joining ? dayjs(formData.date_of_joining) : null}
                                            onChange={(d, ds) => onDateChange("date_of_joining", ds)}
                                            disabled={!isEditMode}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item label="Reporting To">
                                        {isEditMode ? (
                                            <Select
                                                placeholder="Select Reporting to Person"
                                                value={formData.reporting_to || undefined}
                                                loading={reportingToLoading}
                                                showSearch
                                                optionFilterProp="label"
                                                filterOption={(input, option) =>
                                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                onChange={(value) => {
                                                    onSelectChange("reporting_to", value);
                                                }}
                                                notFoundContent={reportingToLoading ? <Spin size="small" /> : "No employees found"}
                                                disabled={reportingToLoading}
                                            >
                                                {reportingTo.map((emp) => (
                                                    <Option
                                                        key={emp.id}
                                                        value={emp.id}
                                                        label={`${emp.first_name} ${emp.last_name} - ${emp.designation_name}`}
                                                    >
                                                        {emp.first_name} {emp.last_name} - {emp.designation_name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Input
                                                value={formData.reporting_to_name || ""}
                                                disabled={!isEditMode}
                                            />
                                        )}
                                    </Form.Item>

                                    <Form.Item label="Role ID">
                                        <Input name="role" value={formData.role || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Status">
                                        <Input value={formData.status || ""} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Probation End Date">
                                        <DatePicker
                                            style={{ width: "100%" }}
                                            value={formData.probation_end_date ? dayjs(formData.probation_end_date) : null}
                                            onChange={(d, ds) => onDateChange("probation_end_date", ds)}
                                            disabled={!isEditMode}
                                        />
                                    </Form.Item>

                                    <Form.Item label="Date of Leaving">
                                        <DatePicker
                                            style={{ width: "100%" }}
                                            value={formData.date_of_leaving ? dayjs(formData.date_of_leaving) : null}
                                            onChange={(d, ds) => onDateChange("date_of_leaving", ds)}
                                            disabled={!isEditMode}
                                        />
                                    </Form.Item>

                                    <Form.Item label="Last Working Day">
                                        <DatePicker
                                            style={{ width: "100%" }}
                                            value={formData.last_working_day ? dayjs(formData.last_working_day) : null}
                                            onChange={(d, ds) => onDateChange("last_working_day", ds)}
                                            disabled={!isEditMode}
                                        />
                                    </Form.Item>
                                    <div style={{
                                        // border: '0.5px solid gray',
                                        // borderRadius: '5px',
                                        // padding: '20px'
                                    }}>
                                        <p style={{ color: 'green' }}>Leave Count for this Year</p>
                                        <Form.Item label="Sick Leave">
                                            <Input name="actual_sick_leave" value={formData.actual_sick_leave || ""} onChange={onInputChange} disabled={!isEditMode} />
                                        </Form.Item>
                                        <Form.Item label="Casual Leave">
                                            <Input name="actual_casual_leave" value={formData.actual_casual_leave || ""} onChange={onInputChange} disabled={!isEditMode} />
                                        </Form.Item>
                                    </div>
                                </Col>
                            </Row>

                            <Space style={{ width: "100%", justifyContent: "space-between" }} className="mt-3">
                                <Button onClick={() => setActiveSection("personal")}>Previous</Button>
                                <Button type="primary" onClick={() => setActiveSection("bank")}>Next</Button>
                            </Space>
                        </Form>
                    </>
                )}

                {activeSection === "calendar" && (
                    <>
                        <LeaveCalendar employee={formData} />
                    </>
                )}
                {activeSection === "leaves" && (
                    <>
                        <ViewOneEmployeeRecord
                            employee={{
                                id: formData.id,
                                employee: formData, // Wrap the employee data
                                leaves_taken_breakdown: {
                                    sick_leave: formData.taken_sick_leave || 0,
                                    casual_leave: formData.taken_casual_leave || 0,
                                    earned_leave: formData.taken_earned_leave || 0,
                                    lop_leave: formData.taken_lop_leave || 0
                                }
                            }}
                            isProfile={false}
                        />
                    </>
                )}
                {activeSection === "permissions" && (
                    <>
                        <ViewOneEmpPerm employee={{
                            id: formData.id,
                            employee: formData, // Wrap the employee data
                            leaves_taken_breakdown: {
                                sick_leave: formData.taken_sick_leave || 0,
                                casual_leave: formData.taken_casual_leave || 0,
                                earned_leave: formData.taken_earned_leave || 0,
                                lop_leave: formData.taken_lop_leave || 0
                            }
                        }}
                            isProfile={false} />
                    </>
                )}

                {/* BANK SECTION */}
                {activeSection === "bank" && (
                    <>
                        <Title level={3} className="text-center mb-4">Bank Details</Title>
                        <Form layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Bank Name">
                                        <Input name="bank_name" value={formData.bank_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Bank Account Number">
                                        <Input name="bank_account_number" value={formData.bank_account_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="CUB Account Number">
                                        <Input name="cub_account_number" value={formData.cub_account_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="IFSC Code">
                                        <Input name="ifsc_code" value={formData.ifsc_code || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item label="Branch Name">
                                        <Input name="branch_name" value={formData.branch_name || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="UAN Number">
                                        <Input name="uan_number" value={formData.uan_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="ESIC IP Number">
                                        <Input name="esic_ip_number" value={formData.esic_ip_number || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Space style={{ width: "100%", justifyContent: "space-between" }} className="mt-3">
                                <Button onClick={() => setActiveSection("employment")}>Previous</Button>
                                <Button type="primary" onClick={() => setActiveSection("address")}>Next</Button>
                            </Space>
                        </Form>
                    </>
                )}

                {/* ADDRESS SECTION */}
                {activeSection === "address" && (
                    <>
                        <Title level={3} className="text-center mb-4">Address Details</Title>

                        <Title level={5}>Present Address</Title>
                        <Form layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Address">
                                        <TextArea name="address_for_communication" value={formData.address_for_communication || ""} onChange={onInputChange} rows={3} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Area">
                                        <Input name="area" value={formData.area || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="City">
                                        <Input name="city" value={formData.city || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item label="State">
                                        <Input name="state" value={formData.state || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Country">
                                        <Input name="country" value={formData.country || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Pincode">
                                        <Input name="pincode" value={formData.pincode || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        <div className="my-3">
                            <Checkbox
                                checked={formData.permanent_address === "yes"}
                                onChange={(e) => {
                                    copyPresentToPermanent(e.target.checked);
                                    setFormData((p) => ({ ...p, permanent_address: e.target.checked ? "yes" : "no" }));
                                }}
                                disabled={!isEditMode}
                            >
                                Same as Present Address
                            </Checkbox>
                        </div>

                        <Title level={5}>Permanent Address</Title>
                        <Form layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Address">
                                        <TextArea name="permanent_address_for_communication" value={formData.permanent_address_for_communication || ""} onChange={onInputChange} rows={3} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Area">
                                        <Input name="permanent_area" value={formData.permanent_area || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="City">
                                        <Input name="permanent_city" value={formData.permanent_city || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item label="State">
                                        <Input name="permanent_state" value={formData.permanent_state || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Country">
                                        <Input name="permanent_country" value={formData.permanent_country || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>

                                    <Form.Item label="Pincode">
                                        <Input name="permanent_pincode" value={formData.permanent_pincode || ""} onChange={onInputChange} disabled={!isEditMode} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        <Space style={{ width: "100%", justifyContent: "space-between" }} className="mt-3">
                            <Button onClick={() => setActiveSection("bank")}>Previous</Button>
                            <Button type="primary" onClick={() => setActiveSection("education")}>Next</Button>
                        </Space>
                    </>
                )}

                {/* EDUCATION SECTION */}
                {activeSection === "education" && (
                    <>
                        <Title level={3} className="text-center mb-4">Academic & Professional Details</Title>

                        <div className="mb-5">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Title level={4}>Education History</Title>
                                <Button type="dashed" icon={<PlusOutlined />} onClick={() => addItem(setEduCount, "education_history")} disabled={!isEditMode}>Add</Button>
                            </div>

                            {Array.from({ length: eduCount }, (_, i) => (
                                <Card key={i} className="mb-3">
                                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                                        <Input placeholder="Qualification" value={formData.education_history?.[i]?.educational_qualification || ""} onChange={(e) => updateArray("education_history", i, "educational_qualification", e.target.value)} disabled={!isEditMode} />
                                        <Input placeholder="Institution" value={formData.education_history?.[i]?.educational_institution_name || ""} onChange={(e) => updateArray("education_history", i, "educational_institution_name", e.target.value)} disabled={!isEditMode} />
                                        <Input placeholder="Degree / Diploma" value={formData.education_history?.[i]?.degree_or_diploma || ""} onChange={(e) => updateArray("education_history", i, "degree_or_diploma", e.target.value)} disabled={!isEditMode} />
                                        <DatePicker style={{ width: "100%" }} placeholder="From" value={formData.education_history?.[i]?.course_duration_from ? dayjs(formData.education_history[i].course_duration_from) : null} onChange={(d, ds) => updateArray("education_history", i, "course_duration_from", ds)} disabled={!isEditMode} />
                                        <DatePicker style={{ width: "100%" }} placeholder="To" value={formData.education_history?.[i]?.course_duration_to ? dayjs(formData.education_history[i].course_duration_to) : null} onChange={(d, ds) => updateArray("education_history", i, "course_duration_to", ds)} disabled={!isEditMode} />
                                        <TextArea placeholder="Extra Curricular Activity" rows={2} value={formData.education_history?.[i]?.extra_curricular_activity || ""} onChange={(e) => updateArray("education_history", i, "extra_curricular_activity", e.target.value)} disabled={!isEditMode} />
                                        <Button danger icon={<CloseOutlined />} onClick={() => removeItem(setEduCount, i, "education_history")} disabled={!isEditMode || eduCount <= 1}>Remove</Button>
                                    </Space>
                                </Card>
                            ))}
                        </div>

                        <div className="mb-5">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Title level={4}>Career History</Title>
                                <Button type="dashed" icon={<PlusOutlined />} onClick={() => addItem(setCareerCount, "career_history")} disabled={!isEditMode}>Add</Button>
                            </div>

                            {Array.from({ length: careerCount }, (_, i) => (
                                <Card key={i} className="mb-3">
                                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                                        <Input placeholder="Company" value={formData.career_history?.[i]?.organisation || ""} onChange={(e) => updateArray("career_history", i, "organisation", e.target.value)} disabled={!isEditMode} />
                                        <Input placeholder="Designation" value={formData.career_history?.[i]?.designation || ""} onChange={(e) => updateArray("career_history", i, "designation", e.target.value)} disabled={!isEditMode} />
                                        <DatePicker style={{ width: "100%" }} placeholder="From" value={formData.career_history?.[i]?.work_duration_from ? dayjs(formData.career_history[i].work_duration_from) : null} onChange={(d, ds) => updateArray("career_history", i, "work_duration_from", ds)} disabled={!isEditMode} />
                                        <DatePicker style={{ width: "100%" }} placeholder="To" value={formData.career_history?.[i]?.work_duration_to ? dayjs(formData.career_history[i].work_duration_to) : null} onChange={(d, ds) => updateArray("career_history", i, "work_duration_to", ds)} disabled={!isEditMode} />
                                        <TextArea placeholder="Key Responsibilities" rows={2} value={formData.career_history?.[i]?.keyresponsibilities || ""} onChange={(e) => updateArray("career_history", i, "keyresponsibilities", e.target.value)} disabled={!isEditMode} />
                                        <Button danger icon={<CloseOutlined />} onClick={() => removeItem(setCareerCount, i, "career_history")} disabled={!isEditMode || careerCount <= 1}>Remove</Button>
                                    </Space>
                                </Card>
                            ))}
                        </div>

                        <div className="mb-5">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Title level={4}>Reference History</Title>
                                <Button type="dashed" icon={<PlusOutlined />} onClick={() => addItem(setRefCount, "reference_history")} disabled={!isEditMode}>Add</Button>
                            </div>

                            {Array.from({ length: refCount }, (_, i) => (
                                <Card key={i} className="mb-3">
                                    <Space direction="vertical" style={{ width: "100%" }} size="middle">
                                        <Input placeholder="Reference Name" value={formData.reference_history?.[i]?.referal_name || ""} onChange={(e) => updateArray("reference_history", i, "referal_name", e.target.value)} disabled={!isEditMode} />
                                        <Input placeholder="Designation" value={formData.reference_history?.[i]?.referal_designation || ""} onChange={(e) => updateArray("reference_history", i, "referal_designation", e.target.value)} disabled={!isEditMode} />
                                        <Input placeholder="Mobile" value={formData.reference_history?.[i]?.referal_mobile_number || ""} onChange={(e) => updateArray("reference_history", i, "referal_mobile_number", e.target.value)} disabled={!isEditMode} />
                                        <Input placeholder="Email" value={formData.reference_history?.[i]?.referal_email_id || ""} onChange={(e) => updateArray("reference_history", i, "referal_email_id", e.target.value)} disabled={!isEditMode} />
                                        <Button danger icon={<CloseOutlined />} onClick={() => removeItem(setRefCount, i, "reference_history")} disabled={!isEditMode || refCount <= 1}>Remove</Button>
                                    </Space>
                                </Card>
                            ))}
                        </div>

                        <Space style={{ width: "100%", justifyContent: "space-between" }} className="mt-3">
                            <Button onClick={() => setActiveSection("address")}>Previous</Button>
                            <Button type="primary" onClick={() => setActiveSection("documents")}>Next</Button>
                        </Space>
                    </>
                )}

                {/* DOCUMENTS SECTION */}
                {activeSection === "documents" && (
                    <>
                        <Title level={3} className="text-center mb-4">Onboarding Documents</Title>
                        <Form layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Aadhaar Card Copy">
                                        {formData.aadhar_card_copy && <Button type="link" onClick={() => openDoc(formData.aadhar_card_copy)}>View Current</Button>}
                                        <Input type="file" name="aadhar_card_copy" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.aadhar_card_copy && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="PAN Card Copy">
                                        {formData.pan_card_copy && <Button type="link" onClick={() => openDoc(formData.pan_card_copy)}>View Current</Button>}
                                        <Input type="file" name="pan_card_copy" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.pan_card_copy && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="SSLC">
                                        {formData.SSLC && <Button type="link" onClick={() => openDoc(formData.SSLC)}>View Current</Button>}
                                        <Input type="file" name="SSLC" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.SSLC && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="HSC / Diploma / ITI">
                                        {formData.HSC_diploma_ITI && <Button type="link" onClick={() => openDoc(formData.HSC_diploma_ITI)}>View Current</Button>}
                                        <Input type="file" name="HSC_diploma_ITI" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.HSC_diploma_ITI && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Degree Certificate">
                                        {formData.degree_certificate && <Button type="link" onClick={() => openDoc(formData.degree_certificate)}>View Current</Button>}
                                        <Input type="file" name="degree_certificate" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.degree_certificate && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="PG / PhD">
                                        {formData.PG_Phd && <Button type="link" onClick={() => openDoc(formData.PG_Phd)}>View Current</Button>}
                                        <Input type="file" name="PG_Phd" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.PG_Phd && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="COVID Certificate">
                                        {formData.covid_certificate && <Button type="link" onClick={() => openDoc(formData.covid_certificate)}>View Current</Button>}
                                        <Input type="file" name="covid_certificate" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.covid_certificate && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Offer Letter">
                                        {formData.offer_letter && <Button type="link" onClick={() => openDoc(formData.offer_letter)}>View Current</Button>}
                                        <Input type="file" name="offer_letter" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.offer_letter && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item label="Appointment Letter">
                                        {formData.appointment_letter && <Button type="link" onClick={() => openDoc(formData.appointment_letter)}>View Current</Button>}
                                        <Input type="file" name="appointment_letter" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.appointment_letter && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Previous Experience Letter">
                                        {formData.previous_experience_letter && <Button type="link" onClick={() => openDoc(formData.previous_experience_letter)}>View Current</Button>}
                                        <Input type="file" name="previous_experience_letter" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.previous_experience_letter && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Relieving Letter">
                                        {formData.releiving_letter && <Button type="link" onClick={() => openDoc(formData.releiving_letter)}>View Current</Button>}
                                        <Input type="file" name="releiving_letter" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.releiving_letter && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Last 3 Months Payslip">
                                        {formData.last_three_month_payslip && <Button type="link" onClick={() => openDoc(formData.last_three_month_payslip)}>View Current</Button>}
                                        <Input type="file" name="last_three_month_payslip" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.last_three_month_payslip && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Bank Account Details">
                                        {formData.bank_account_details && <Button type="link" onClick={() => openDoc(formData.bank_account_details)}>View Current</Button>}
                                        <Input type="file" name="bank_account_details" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.bank_account_details && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Last 3 Months Bank Statement">
                                        {formData.last_three_months_account_statement && <Button type="link" onClick={() => openDoc(formData.last_three_months_account_statement)}>View Current</Button>}
                                        <Input type="file" name="last_three_months_account_statement" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.last_three_months_account_statement && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Others 1">
                                        {formData.others_1 && <Button type="link" onClick={() => openDoc(formData.others_1)}>View Current</Button>}
                                        <Input type="file" name="others_1" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.others_1 && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>

                                    <Form.Item label="Others 2">
                                        {formData.others2 && <Button type="link" onClick={() => openDoc(formData.others2)}>View Current</Button>}
                                        <Input type="file" name="others2" onChange={onInputChange} disabled={!isEditMode} />
                                        {fileData.others2 && <div style={{ color: 'green' }}>New file selected</div>}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Space style={{ width: "100%", justifyContent: "space-between" }} className="mt-3">
                                <Button onClick={() => setActiveSection("education")}>Previous</Button>
                            </Space>
                        </Form>
                    </>
                )}

                {/* UPDATE SECTION */}
                {activeSection === "update" && (
                    <Card title="Update Employee Permissions" bordered={false}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Switch checked={formData.is_ot || false} onChange={(val) => setFormData({ ...formData, is_ot: val })} /> Can Apply OT
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_special || false} onChange={(val) => setFormData({ ...formData, is_special: val })} /> Special Case
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_hr || false} onChange={(val) => setFormData({ ...formData, is_hr: val })} /> HR Team
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_hod || false} onChange={(val) => setFormData({ ...formData, is_hod: val })} /> HOD
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_superuser || false} onChange={(val) => setFormData({ ...formData, is_superuser: val })} /> GM HR
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_management || false} onChange={(val) => setFormData({ ...formData, is_management: val })} /> Management
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_shift || false} onChange={(val) => setFormData({ ...formData, is_shift: val })} /> Is Shift
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_checkin || false} onChange={(val) => setFormData({ ...formData, is_checkin: val })} /> Is Checkin
                            </Col>
                            <Col span={12}>
                                <Switch checked={formData.is_active || false} onChange={(val) => setFormData({ ...formData, is_active: val })} /> Is Active
                            </Col>
                        </Row>
                        <br />
                        <center>
                            <Select
                                placeholder="Reporting To Person"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                style={{ width: "50%" }}
                                value={formData.reporting_to || undefined}
                                onChange={(val) => setFormData({ ...formData, reporting_to: val })}
                                filterOption={(input, option) => option?.children?.toString().toLowerCase().includes(input.toLowerCase())}
                                loading={loadingEmployees}
                                notFoundContent={loadingEmployees ? <Spin size="medium" /> : "No employees found"}
                                disabled={loadingEmployees}
                            >
                                {employees.map((emp) => (
                                    <Option key={emp.id} value={emp.id}>
                                        {emp.user?.first_name} {emp.user?.last_name} - {emp.department_name}
                                    </Option>
                                ))}
                            </Select>
                        </center>
                        <br />
                        <center>
                            <Button style={{ background: '#0e497b', color: 'white' }} type="primary" loading={isSubmitting} onClick={handleUpdateEmployee}>
                                Update Employee
                            </Button>
                        </center>
                    </Card>
                )}

                {
                    activeSection === "sendNote" && (
                        <>
                            {formData.id ? (
                                <SendMail employee={formData} />
                            ) : (
                                <div className="text-center py-5">
                                    <Spin tip="Loading employee data..." />
                                </div>
                            )}
                        </>
                    )
                }

                {
                    activeSection === "triggerNoc" && (
                        <>
                            {formData.id ? (
                                <>
                                    {
                                        !authData?.employee?.is_hr && (
                                            <>
                                                <center><Button onClick={triggerExitInterview}>Trigger Exit Interview</Button></center>
                                            </>
                                        )

                                    }
                                    <NocForm employee={formData} />

                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <Spin tip="Loading employee data..." />
                                </div>
                            )}
                        </>
                    )
                }

                {
                    activeSection === "exitInterview" && (
                        <>
                            {formData.id ? (
                                <>
                                    <ViewOneExit employeeId={employeeId} />
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <Spin tip="Loading employee data..." />
                                </div>
                            )}
                        </>
                    )
                }

                {
                    activeSection === "status" && (
                        <>
                            <Card
                                title=""
                                style={{
                                    maxWidth: 700,
                                    margin: "30px auto",
                                    borderRadius: 12,
                                }}
                            >
                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={onFinish}
                                    initialValues={{
                                        employee: 52,
                                        requested_status: "NOTICE_PERIOD",
                                        date_of_leaving: dayjs(""),
                                        last_working_day: dayjs(""),
                                        remarks: "",
                                    }}
                                >
                                    <Form.Item
                                        label="Requested Status"
                                        name="requested_status"
                                        rules={[{ required: true, message: "Please select status" }]}
                                    >
                                        <Select placeholder="Select Status">
                                            {employeeStatuses.map((status) => (
                                                <Option key={status.value} value={status.value}>
                                                    {status.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    {/* PROBATION: only probation_end_date */}
                                    {requestedStatus === "PROBATION" && (
                                        <Form.Item
                                            label="Probation End Date"
                                            name="probation_end_date"
                                            rules={[{ required: true, message: "Please select probation end date" }]}
                                        >
                                            <DatePicker style={{ width: "100%" }} />
                                        </Form.Item>
                                    )}

                                    {/* CONFIRMED: only final_probation_end_date */}
                                    {requestedStatus === "CONFIRMED" && (
                                        <Form.Item
                                            label="Confirmed Date"
                                            name="final_probation_end_date"
                                            rules={[{ required: true, message: "Please select final probation end date" }]}
                                        >
                                            <DatePicker style={{ width: "100%" }} />
                                        </Form.Item>
                                    )}

                                    {/* All other statuses: date_of_leaving + last_working_day */}
                                    {requestedStatus && !["PROBATION", "CONFIRMED"].includes(requestedStatus) && (
                                        <>
                                            <Form.Item
                                                label="Date of Leaving"
                                                name="date_of_leaving"
                                                rules={[{ required: true, message: "Please select date" }]}
                                            >
                                                <DatePicker style={{ width: "100%" }} />
                                            </Form.Item>

                                            <Form.Item
                                                label="Last Working Day"
                                                name="last_working_day"
                                                rules={[{ required: true, message: "Please select date" }]}
                                            >
                                                <DatePicker style={{ width: "100%" }} />
                                            </Form.Item>
                                        </>
                                    )}

                                    <Form.Item
                                        label="Remarks"
                                        name="remarks"
                                        rules={[{ required: true, message: "Please enter remarks" }]}
                                    >
                                        <TextArea rows={4} placeholder="Enter remarks" />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            style={{ backgroundColor: "#0e497b", color: "white" }}
                                            block
                                        >
                                            Submit Request
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </>
                    )
                }


                {activeSection === "appointment" && (
                    <>
                        <center>
                            <Button type="primary" style={{ background: '#0e497b', color: 'white' }} onClick={() => setShowUpload(true)}>
                                Preview Appointment
                            </Button>
                        </center>

                        <Modal
                            open={showUpload}
                            onCancel={() => setShowUpload(false)}
                            footer={null}
                            centered
                        >
                            <center>
                                <Upload {...uploadProps}>
                                    <Button icon={<UploadOutlined />}>
                                        Upload Signature
                                    </Button>
                                </Upload>
                            </center>


                            {signatureUrl && (
                                <div style={{ marginTop: 20, textAlign: "center" }}>
                                    <Image
                                        src={signatureUrl}
                                        width={200}
                                        alt="Signature"
                                    />
                                    <br />
                                    <br />

                                    <Button
                                        type="default"
                                        loading={loading}
                                        onClick={async () => {
                                            if (!image) {
                                                message.error("Please upload signature image first");
                                                return;
                                            }

                                            try {
                                                setLoading(true);

                                                const formData = new FormData();
                                                formData.append("signature_image", image);
                                                // formData.append("mode", "preview"); // 👈 backend flag

                                                const res = await axios.put(
                                                    `${backUrl}/api/emp/appointment/upload-preview/${employeeId}/`,
                                                    formData,
                                                    {
                                                        headers: {
                                                            Authorization: `Bearer ${authData?.access}`,
                                                        },
                                                        responseType: "blob",
                                                    }
                                                );

                                                const blob = new Blob([res.data], { type: "application/pdf" });
                                                const url = window.URL.createObjectURL(blob);

                                                setPreviewUrl(url);
                                                message.success("Preview generated");
                                            } catch (err) {
                                                console.error(err);

                                                if (err.response && err.response.data instanceof Blob) {
                                                    const reader = new FileReader();

                                                    reader.onload = () => {
                                                        try {
                                                            const errorData = JSON.parse(reader.result);
                                                            alert(errorData.message || "Preview failed");
                                                        } catch {
                                                            alert("Preview failed");
                                                        }
                                                    };

                                                    reader.readAsText(err.response.data);
                                                } else {
                                                    alert(err.response?.data?.message || "Preview failed");
                                                }
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    >
                                        Preview
                                    </Button>
                                    {previewUrl && (
                                        <>
                                            <iframe
                                                src={previewUrl}
                                                width="100%"
                                                height="500px"
                                                style={{ border: "1px solid #ddd", marginTop: 16 }}
                                                title="Appointment Preview"
                                            />

                                            <br />

                                            <Button
                                                type="primary"
                                                style={{ marginTop: 16 }}
                                                onClick={async () => {
                                                    try {
                                                        const formData = new FormData();
                                                        formData.append("signature_image", image);
                                                        formData.append("mode", "download");

                                                        const res = await axios.put(
                                                            `${backUrl}/api/emp/appointment/upload-preview/${employeeId}/`,
                                                            formData,
                                                            {
                                                                headers: {
                                                                    Authorization: `Bearer ${authData?.access}`,
                                                                },
                                                                responseType: "blob",
                                                            }
                                                        );

                                                        const blob = new Blob([res.data], {
                                                            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                                        });

                                                        const url = window.URL.createObjectURL(blob);

                                                        const link = document.createElement("a");
                                                        link.href = url;
                                                        link.download = "Appointment_Letter.docx";
                                                        document.body.appendChild(link);
                                                        link.click();

                                                        document.body.removeChild(link);
                                                        window.URL.revokeObjectURL(url);

                                                        message.success("Word file downloaded");
                                                        setShowUpload(false);
                                                    } catch (err) {
                                                        console.error(err);
                                                        message.error("Download failed");
                                                    }
                                                }}
                                            >
                                                Done & Download
                                            </Button>
                                        </>
                                    )}




                                </div>
                            )}
                        </Modal>
                    </>
                )}

            </div>

            {/* ---------- IMAGE PREVIEW MODAL ---------- */}
            <Modal
                open={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
                width={800}
                centered
            >
                <img src={docUrl} alt="Document" style={{ width: "100%" }} />
            </Modal>
        </>
    );
};

export default EditOneEmployee;


















